import { useState } from 'react'
import { TrendingUp, TrendingDown, Minus, Globe, ChevronDown, ChevronRight } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'

const DIRECTION_CONFIG = {
  rising:  { icon: TrendingUp,   color: 'text-slate-lighter', label: 'Subiendo',  bg: 'bg-slate-darker' },
  falling: { icon: TrendingDown, color: 'text-red-400',       label: 'Bajando',   bg: 'bg-slate-darker' },
  stable:  { icon: Minus,        color: 'text-slate-light',   label: 'Estable',   bg: 'bg-slate-darker' },
}

const RECURRENCE_LABELS = {
  monthly:  { label: 'Mensual',    desc: 'Se compra cada mes — ideal para Ads' },
  regular:  { label: 'Regular',    desc: 'Compra frecuente, demanda sostenida' },
  periodic: { label: 'Periódico',  desc: 'Compra en períodos específicos' },
  seasonal: { label: 'Estacional', desc: 'Solo en ciertos momentos del año' },
  sporadic: { label: 'Esporádico', desc: 'Compra irregular, evaluar bien' },
  unknown:  { label: 'N/D',        desc: 'Datos insuficientes' },
}

const SEASONALITY_LABELS = {
  evergreen:         { label: 'Evergreen',  color: 'text-slate-lighter' },
  moderate:          { label: 'Moderado',   color: 'text-slate-light' },
  seasonal:          { label: 'Estacional', color: 'text-yellow-400' },
  insufficient_data: { label: 'N/D',        color: 'text-slate-medium' },
  unknown:           { label: 'N/D',        color: 'text-slate-medium' },
}

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-darker border border-slate-dark/40 rounded px-3 py-2 text-xs text-slate-lightest">
        Interés: <span className="font-bold">{payload[0].value}</span>/100
      </div>
    )
  }
  return null
}

export default function GoogleTrendsPanel({ trendsData }) {
  const [expandedCountry, setExpandedCountry] = useState(null)

  if (!trendsData) return null

  const { keyword, global_trend = {}, countries = {}, related_queries = [],
          seasonality = 'unknown', recurrence = 'unknown', trend_direction = 'stable',
          is_mock, source = '' } = trendsData

  const dirConf = DIRECTION_CONFIG[trend_direction] || DIRECTION_CONFIG.stable
  const DirIcon = dirConf.icon

  // Build chart data from series
  const series = global_trend.series || []
  const chartData = series.map((val, i) => ({ week: `S${i + 1}`, interest: val }))

  const recurrenceInfo = RECURRENCE_LABELS[recurrence] || RECURRENCE_LABELS.unknown
  const seasonalityInfo = SEASONALITY_LABELS[seasonality] || SEASONALITY_LABELS.unknown

  const countryList = Object.entries(countries).slice(0, 6)

  return (
    <div className="gradient-glow p-6 rounded-xl card-hover">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <TrendingUp className="w-5 h-5 text-slate-lighter" />
        <h3 className="text-lg font-bold text-white">Google Trends</h3>
        <span className={`text-xs px-2.5 py-0.5 rounded-full border ml-auto font-medium ${
          is_mock
            ? 'bg-slate-darker border-slate-dark/40 text-slate-medium'
            : 'bg-slate-dark border-slate-medium/40 text-slate-lighter'
        }`}>
          {is_mock ? 'Estimación estadística' : source === 'rapidapi-google-trends' ? '✓ Datos reales (RapidAPI)' : '✓ Datos reales (pytrends)'}
        </span>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {/* Trend direction */}
        <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
          <DirIcon className={`w-5 h-5 mx-auto mb-1 ${dirConf.color}`} />
          <p className={`text-base font-bold ${dirConf.color}`}>{dirConf.label}</p>
          <p className="text-xs text-slate-light">Tendencia</p>
        </div>

        {/* Avg interest */}
        <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
          <p className="text-2xl font-bold text-slate-lighter">{global_trend.avg || 0}</p>
          <p className="text-xs text-slate-light">Interés promedio</p>
          <p className="text-xs text-slate-medium">/100</p>
        </div>

        {/* Seasonality */}
        <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
          <p className={`text-base font-bold ${seasonalityInfo.color}`}>{seasonalityInfo.label}</p>
          <p className="text-xs text-slate-light">Estacionalidad</p>
        </div>

        {/* Recurrence */}
        <div className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg text-center">
          <p className="text-base font-bold text-slate-lighter">{recurrenceInfo.label}</p>
          <p className="text-xs text-slate-light">Recurrencia</p>
        </div>
      </div>

      {/* Recurrence insight */}
      <div className="mb-5 p-3 bg-slate-darker border border-slate-dark/20 rounded-lg">
        <p className="text-xs text-slate-300">
          <span className="font-semibold text-slate-lighter">Patrón de compra: </span>
          {recurrenceInfo.desc}
          {global_trend.change_pct !== undefined && (
            <span className={`ml-2 font-semibold ${global_trend.change_pct >= 0 ? 'text-slate-lighter' : 'text-red-400'}`}>
              ({global_trend.change_pct >= 0 ? '+' : ''}{global_trend.change_pct}% vs período anterior)
            </span>
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Chart */}
        {chartData.length > 0 && (
          <div>
            <p className="text-xs text-slate-light font-medium mb-2 uppercase tracking-wide">Interés global en el tiempo</p>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(122,122,122,0.08)" />
                <XAxis dataKey="week" tick={{ fill: '#7a7a7a', fontSize: 10 }} interval={Math.floor(chartData.length / 6)} />
                <YAxis domain={[0, 100]} tick={{ fill: '#7a7a7a', fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="interest"
                  stroke="#a8a8a8"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: '#d0d0d0' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Per-country breakdown with city drill-down */}
        {countryList.length > 0 && (
          <div>
            <p className="text-xs text-slate-light font-medium mb-2 uppercase tracking-wide">Interés por país</p>
            <div className="space-y-1">
              {countryList.map(([country, data]) => {
                const avg = data.avg || 0
                const dir = data.direction || 'stable'
                const DIcon = DIRECTION_CONFIG[dir]?.icon || Minus
                const cities = data.cities || []
                const isExpanded = expandedCountry === country
                return (
                  <div key={country}>
                    <div
                      className={`flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors ${cities.length ? 'cursor-pointer hover:bg-slate-darker/60' : ''}`}
                      onClick={() => cities.length && setExpandedCountry(isExpanded ? null : country)}
                    >
                      <div className="flex items-center gap-1 w-12 shrink-0">
                        {cities.length > 0
                          ? (isExpanded
                              ? <ChevronDown className="w-3 h-3 text-slate-medium" />
                              : <ChevronRight className="w-3 h-3 text-slate-medium" />)
                          : <span className="w-3" />
                        }
                        <span className="text-xs font-mono text-slate-lighter">{country}</span>
                      </div>
                      <div className="flex-1 h-1.5 bg-slate-medium rounded-full overflow-hidden">
                        <div
                          className="h-full bg-slate-lighter rounded-full transition-all"
                          style={{ width: `${avg}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-lighter w-6 text-right">{avg}</span>
                      <DIcon className={`w-3 h-3 ${DIRECTION_CONFIG[dir]?.color}`} />
                    </div>

                    {/* City breakdown */}
                    {isExpanded && cities.length > 0 && (
                      <div className="ml-14 mt-1 mb-2 space-y-1 border-l border-slate-dark/40 pl-3">
                        {cities.map(city => (
                          <div key={city.name} className="flex items-center gap-2">
                            <span className="text-xs text-slate-light w-28 truncate">{city.name}</span>
                            <div className="flex-1 h-1 bg-slate-darker rounded-full overflow-hidden">
                              <div
                                className="h-full bg-slate-light rounded-full transition-all"
                                style={{ width: `${city.interest}%` }}
                              />
                            </div>
                            <span className="text-xs text-slate-medium w-6 text-right">{city.interest}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {/* Related rising queries */}
      {related_queries.length > 0 && (
        <div className="mt-4">
          <p className="text-xs text-slate-light font-medium mb-2 uppercase tracking-wide">Búsquedas relacionadas en alza</p>
          <div className="flex flex-wrap gap-2">
            {related_queries.slice(0, 8).map((q, i) => (
              <span
                key={i}
                className="text-xs px-2.5 py-1 bg-slate-darker border border-slate-dark/30 text-slate-lighter rounded-full"
              >
                {q.query || q.term}
                {q.value && <span className="ml-1 text-slate-medium">+{q.value}%</span>}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
